import merge from "lodash/merge";
//@ts-ignore
import DiskStorage from "disk-storage/disk-storage";
import {
  BasicShadowMap,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  MeshToonMaterial,
  PCFSoftShadowMap,
  ShadowMapType,
  VSMShadowMap,
} from "three";
import { DeepPartial } from "../../lib/misc/util";

export const hasLocalStorage =
  typeof window !== "undefined" && window.localStorage;

export type MaterialSpec = "basic" | "lit" | "full";
export type MaterialCtor =
  | typeof MeshBasicMaterial
  | typeof MeshLambertMaterial
  | typeof MeshToonMaterial
  | typeof MeshStandardMaterial;
export type PlayerLODSpec = 0 | 1 | 2 | 3;

export type UserOptionPreset = "low" | "mid" | "high" | "custom";

export type ShadowType = "basic" | "soft" | "vsm" | "csm" | "none";

export type EffectsType = "dof" | "outline" | "none";

export type UserOptionsSet = {
  presetName: UserOptionPreset;
  shadows: {
    type: ShadowType;
    cascades: number;
  };
  sky: {
    dynamic: boolean;
    clouds: boolean;
    staticTexture: boolean;
  };

  playerLOD: PlayerLODSpec;
  materials: {
    spec: MaterialSpec;
  };
  effects: {
    // TODO: make these *not* mutually exclusive... i.e.
    // dof: boolean;
    // outline: boolean;
    dofOrOutline: EffectsType;
  };
};

export class UserOptions {
  values: UserOptionsSet;

  private storageSettings = {
    namespace: "poser",
    itemName: "options",
    expiry: 60 * 60 * 24 * 7,
  };

  constructor(
    namespaceOverride?: string,
    optionsOverrides?: DeepPartial<UserOptionsSet>
  ) {
    if (namespaceOverride) {
      this.storageSettings.namespace = `${this.storageSettings.namespace}-${namespaceOverride}`;
    }

    this.values = this._loadPreset("low");

    // get cached options
    const options = this._loadSaved();

    if (options) {
      // console.log("saved", options);
      this.values = merge(this.values, options);
    }

    if (optionsOverrides) {
      // console.log("optionsOverrides", optionsOverrides);
      this.values = merge(this.values, optionsOverrides);
    }
  }

  loadPreset(preset: UserOptionPreset) {
    console.log("LOAD!", preset);
    this.values = this._loadPreset(preset);
  }

  loadSaved() {
    this.values = merge(this.values, this._loadSaved());
  }

  save() {
    const serialised = Object.assign({}, this.values);
    //@ts-ignore
    serialised.materials.ctor = undefined;
    DiskStorage.put(
      this.storageSettings.namespace,
      this.storageSettings.itemName,
      serialised,
      this.storageSettings.expiry
    );
  }

  reset() {
    DiskStorage.delete(
      this.storageSettings.namespace,
      this.storageSettings.itemName
    );
  }

  asUrlParams(): string {
    const options = [
      `user.shadow.type=${this.values.shadows.type.toString()}`,
      `user.shadow.cascades=${this.values.shadows.cascades.toString()}`,
      `user.sky.dynamic=${this.values.sky.dynamic.toString()}`,
      `user.sky.clouds=${this.values.sky.clouds.toString()}`,
      `user.sky.staticTexture=${this.values.sky.staticTexture.toString()}`,
      `user.playerLOD=${this.values.playerLOD.toString()}`,
      `user.materialSpec=${this.values.materials.spec.toString()}`,
      `user.effects.dofOrOutline=${this.values.effects.dofOrOutline.toString()}`,
    ];

    return options.join("&");
  }

  private _loadSaved(): DeepPartial<UserOptionsSet> | undefined {
    const options = hasLocalStorage
      ? DiskStorage.get(
          this.storageSettings.namespace,
          this.storageSettings.itemName
        )
      : undefined;
    return options.value;
  }

  private _loadPreset(preset: UserOptionPreset): UserOptionsSet {
    switch (preset) {
      case "high": {
        const defaultMat: MaterialSpec = "full";
        return {
          presetName: "high",
          shadows: {
            type: "vsm",
            cascades: 3,
          },
          sky: {
            dynamic: true,
            clouds: true,
            staticTexture: false,
          },
          playerLOD: 0,
          materials: {
            spec: defaultMat,
          },
          effects: {
            dofOrOutline: "none",
          },
        };
      }

      case "mid": {
        const defaultMat: MaterialSpec = "lit";
        return {
          presetName: "mid",
          shadows: {
            type: "soft",
            cascades: 3,
          },
          sky: {
            dynamic: true,
            clouds: true,
            staticTexture: false,
          },
          playerLOD: 3,
          materials: {
            spec: defaultMat,
          },
          effects: {
            dofOrOutline: "none",
          },
        };
      }

      case "low":
      default: {
        const defaultMat: MaterialSpec = "basic";
        return {
          presetName: "low",
          shadows: {
            type: "none",
            cascades: 3,
          },
          sky: {
            dynamic: false,
            clouds: false,
            staticTexture: true,
          },
          playerLOD: 3,
          materials: {
            spec: defaultMat,
          },
          effects: {
            dofOrOutline: "none",
          },
        };
      }
    }
  }

  hasShadows(): boolean {
    return ["soft", "csm", "basic", "vsm"].includes(this.values.shadows.type);
  }

  getMaterialCtor(): {
    player: MaterialCtor;
    stadium: MaterialCtor;
  } {
    let player: MaterialCtor;
    let stadium: MaterialCtor;
    switch (this.values.materials.spec) {
      case "full":
        player = MeshToonMaterial;
        stadium = MeshStandardMaterial;
        break;
      case "lit":
        player = MeshToonMaterial;
        stadium = MeshLambertMaterial;
        break;
      case "basic":
      default:
        player = MeshBasicMaterial;
        stadium = MeshBasicMaterial;
        break;
    }

    return { player, stadium };
  }

  getShadowMapType(): ShadowMapType {
    switch (this.values.shadows.type) {
      case "soft":
        return PCFSoftShadowMap;
      case "csm":
      case "vsm":
        return VSMShadowMap;
      case "basic":
      default:
        return BasicShadowMap;
    }
  }

  static optionSetFromUrlParams(
    params: URLSearchParams
  ): DeepPartial<UserOptionsSet> {
    const shadowType = params.get("user.shadows.type") ?? undefined;
    const shadowCascades = params.get("user.shadows.cascades");
    const skyDynamic = params.get("user.sky.dynamic");
    const skyClouds = params.get("user.sky.clouds");
    const skyStaticTexture = params.get("user.sky.staticTexture");
    const playerLOD = params.get("user.playerLOD");
    const materialSpec = params.get("user.materialSpec") ?? undefined;
    const dofOrOutline = params.get("user.effects.dofOrOutline") ?? undefined;

    return {
      presetName: "custom",
      shadows: {
        type: shadowType as ShadowType | undefined,
        cascades: getInt(shadowCascades),
      },
      sky: {
        dynamic: getBool(skyDynamic),
        clouds: getBool(skyClouds),
        staticTexture: getBool(skyStaticTexture),
      },
      playerLOD: getInt(playerLOD) as PlayerLODSpec | undefined,
      materials: {
        spec: materialSpec as MaterialSpec | undefined,
      },
      effects: {
        dofOrOutline: dofOrOutline as EffectsType | undefined,
      },
    };
  }
}

function getBool(v: string | null): boolean | undefined {
  return v === "true" ? true : v === "false" ? false : undefined;
}

function getInt(v: string | null): number | undefined {
  if (v === undefined || v === null) return undefined;

  const n = Number.parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
}
